import { useMemo } from 'react';
import { diffLines } from 'diff';

interface JsonDiffProps {
  original: string;
  updated: string;
  /** Side-by-side (two columns) or inline (single column). */
  variant?: 'split' | 'inline';
  className?: string;
}

/**
 * Pretty-print a JSON string if possible. Returning the input unchanged on
 * parse failure keeps the diff useful even while the user is mid-edit.
 */
function pretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

/**
 * Render a line-level diff between two JSON blobs. Each pane shows the
 * matching side only — lines unique to the other side render as a
 * greyed-out placeholder so row heights stay aligned across the gap.
 */
export function JsonDiff({
  original,
  updated,
  variant = 'split',
  className = '',
}: JsonDiffProps) {
  const rows = useMemo(() => buildDiffRows(pretty(original), pretty(updated)), [
    original,
    updated,
  ]);

  if (variant === 'inline') {
    return (
      <pre className={`json-diff json-diff--inline ${className}`}>
        {rows.map((row, i) => (
          <span key={i} className={`json-diff__row json-diff__row--${row.kind}`}>
            <span className="json-diff__marker">
              {row.kind === 'add' ? '+' : row.kind === 'remove' ? '−' : ' '}
            </span>
            {row.kind === 'add'
              ? row.right ?? ''
              : row.kind === 'remove'
                ? row.left ?? ''
                : row.left ?? ''}
            {'\n'}
          </span>
        ))}
      </pre>
    );
  }

  return (
    <div className={`json-diff json-diff--split ${className}`}>
      <pre className="json-diff__pane json-diff__pane--left">
        {rows.map((row, i) => (
          <span
            key={`l-${i}`}
            className={`json-diff__row json-diff__row--${
              row.kind === 'remove' || row.kind === 'equal' ? row.kind : 'gap'
            }`}
          >
            {row.left ?? ' '}
            {'\n'}
          </span>
        ))}
      </pre>
      <pre className="json-diff__pane json-diff__pane--right">
        {rows.map((row, i) => (
          <span
            key={`r-${i}`}
            className={`json-diff__row json-diff__row--${
              row.kind === 'add' || row.kind === 'equal' ? row.kind : 'gap'
            }`}
          >
            {row.right ?? ' '}
            {'\n'}
          </span>
        ))}
      </pre>
    </div>
  );
}

interface DiffRow {
  kind: 'equal' | 'add' | 'remove';
  left?: string;
  right?: string;
}

/**
 * Translate `diffLines` parts into aligned left/right rows so the split
 * view keeps the two panes in lockstep — each row renders one line on
 * exactly one side (with the opposite side left blank) except for `equal`
 * rows which show the same text on both sides.
 */
function buildDiffRows(original: string, updated: string): DiffRow[] {
  const parts = diffLines(original, updated, { newlineIsToken: false });
  const rows: DiffRow[] = [];

  for (const part of parts) {
    const lines = part.value.replace(/\n$/, '').split('\n');
    if (part.added) {
      for (const line of lines) rows.push({ kind: 'add', right: line });
    } else if (part.removed) {
      for (const line of lines) rows.push({ kind: 'remove', left: line });
    } else {
      for (const line of lines) rows.push({ kind: 'equal', left: line, right: line });
    }
  }

  return rows;
}
